import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface MakeCallRequest {
  to: string;
  from_number: string;
  caller_id: string;
  patient_id?: string;
}

export interface MakeCallResponse {
  success?: boolean;
  call_id?: string;
  message?: string;
  error?: string;
  Call?: any;
  RestException?: {
    Status?: number;
    Message?: string;
  };
}

export interface ExotelCallLog {
  Sid: string;
  ParentCallSid: string;
  DateCreated: string;
  DateUpdated: string;
  AccountSid: string;
  To: string;
  From: string;
  PhoneNumber: string;
  PhoneNumberSid: string;
  /** All possible statuses from the Exotel callback webhook */
  Status: 'completed' | 'busy' | 'failed' | 'no-answer' | 'ringing' | 'in-progress';
  StartTime: string;
  EndTime: string;
  /** Total call duration in seconds */
  Duration: number;
  /** Actual talk/conversation duration in seconds (from Exotel callback) */
  ConversationDuration?: number;
  Price: number;
  Direction: 'inbound' | 'outbound-api' | 'outbound';
  AnsweredBy: string;
  ForwardedFrom: string;
  CallerName: string;
  Uri: string;
  CustomField: string;
  RecordingUrl: string;
  // Additional fields from API
  patient_document_id?: string;
  patient_id?: string;
  patient_image?: string | null;
  patient_name?: string;
  telecaller_document_id?: string;
  user_id?: string;
  telecaller_image?: string | null;
  telecaller_name?: string;
}

/**
 * Pagination block returned alongside the call list. The backend forwards Exotel's own
 * metadata, so the keys are PascalCase (`Total`, `PageSize`) with a lowercase `total` alias.
 */
export interface ExotelCallLogsMetadata {
  total?: number;
  Total?: number;
  PageSize?: number;
  FirstPageUri?: string;
  PrevPageUri?: string;
  NextPageUri?: string;
  [key: string]: any;
}

export interface ExotelCallLogsResponse {
  calls: ExotelCallLog[];
  total: number;
  metadata?: ExotelCallLogsMetadata;
}

/**
 * Reads the record count out of a call-logs response, whichever casing the backend used.
 * Falls back to the number of rows received when no metadata is present.
 */
export function getCallLogsTotal(response: { calls?: any[]; total?: number; metadata?: ExotelCallLogsMetadata } | null): number {
  const meta = response?.metadata;
  const total = meta?.Total ?? meta?.total ?? response?.total;
  return typeof total === 'number' ? total : (response?.calls?.length ?? 0);
}

/**
 * Shape of the real-time status and details payload returned by GET /api/v1/exotel/call/{call_sid}
 */
export interface ExotelCallDetail {
  Sid?: string;
  ParentCallSid?: string | null;
  DateCreated?: string;
  DateUpdated?: string;
  AccountSid?: string;
  To?: string;
  From?: string;
  PhoneNumber?: string;
  PhoneNumberSid?: string;
  Status?: string;
  StartTime?: string;
  EndTime?: string;
  Duration?: number | string;
  ConversationDuration?: number | string;
  Price?: number | string;
  Direction?: string;
  AnsweredBy?: string | null;
  ForwardedFrom?: string | null;
  CallerName?: string | null;
  Uri?: string;
  RecordingUrl?: string | null;
  [key: string]: any;
}

export interface ExotelCallDetailsResponse {
  Call?: ExotelCallDetail;
  Sid?: string;
  CallSid?: string;
  Status?: string;
  status?: string;
  Duration?: number | string;
  ConversationDuration?: number | string;
  StartTime?: string;
  EndTime?: string;
  [key: string]: any;
}

export type ExotelCallStatusPayload = ExotelCallDetailsResponse;

export interface CallLogsQueryParams {
  start_time?: string;
  end_time?: string;
  status?: 'completed' | 'busy' | 'failed' | 'no-answer';
  direction?: 'inbound' | 'outbound';
  /** 1-based page number */
  page?: number;
  /** Records per page (maps to Exotel's PageSize) */
  page_size?: number;
}

export interface TelecallerCallLogsParams extends CallLogsQueryParams {
  max_records?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ExotelApiService {
  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all call logs (paginated server-side)
   */
  getCallLogs(params?: CallLogsQueryParams): Observable<ExotelCallLogsResponse> {
    return this.http.get<ExotelCallLogsResponse>(`${this.baseUrl}/exotel/call-logs/all`, {
      params: this.buildCallLogsParams(params)
    });
  }

  /**
   * Shared query-string builder for both call-log endpoints.
   */
  private buildCallLogsParams(params?: CallLogsQueryParams): HttpParams {
    let httpParams = new HttpParams();
    if (!params) return httpParams;

    if (params.start_time) httpParams = httpParams.set('start_time', params.start_time);
    if (params.end_time) httpParams = httpParams.set('end_time', params.end_time);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.direction) httpParams = httpParams.set('direction', params.direction);
    if (params.page) httpParams = httpParams.set('page', params.page.toString());
    if (params.page_size) {
      // The exotel endpoints echo Exotel's own `PageSize` metadata while the rest of the API
      // pages with `limit`, so send both spellings until the backend contract is confirmed.
      httpParams = httpParams
        .set('page_size', params.page_size.toString())
        .set('limit', params.page_size.toString());
    }

    return httpParams;
  }

  /**
   * Get call logs for a specific telecaller
   */
  getTelecallerCallLogs(telecallerId: string, params?: TelecallerCallLogsParams): Observable<ExotelCallLogsResponse> {
    let httpParams = this.buildCallLogsParams(params);

    if (params?.max_records) {
      httpParams = httpParams.set('max_records', params.max_records.toString());
    }

    return this.http.get<ExotelCallLogsResponse>(
      `${this.baseUrl}/telecallers/${telecallerId}/call-logs`,
      { params: httpParams }
    );
  }

  /**
   * Get proxied call recording URL (streams MP3 without exposing Exotel credentials)
   * @param recordingUrl - The full Exotel recording URL from call logs
   * @param download - Set to true to download the file, false to play inline (default: false)
   */
  getProxiedRecordingUrl(recordingUrl: string, download: boolean = false): string {
    let params = new HttpParams().set('recording_url', recordingUrl);
    if (download) {
      params = params.set('download', 'true');
    }
    return `${this.baseUrl}/exotel/recording?${params.toString()}`;
  }

  /**
   * Get download URL for call recording
   * @param recordingUrl - The full Exotel recording URL from call logs
   */
  getRecordingDownloadUrl(recordingUrl: string): string {
    return this.getProxiedRecordingUrl(recordingUrl, true);
  }

  /**
   * Fetch call recording as blob with authentication
   * @param recordingUrl - The full Exotel recording URL from call logs
   * @param download - Set to true to download the file
   */
  getRecordingBlob(recordingUrl: string, download: boolean = false): Observable<Blob> {
    let params = new HttpParams().set('recording_url', recordingUrl);
    if (download) {
      params = params.set('download', 'true');
    }

    return this.http.get(`${this.baseUrl}/exotel/recording`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Initiate a voice call using Exotel
   */
  makeCall(callData: MakeCallRequest): Observable<MakeCallResponse> {
    let params = new HttpParams()
      .set('to', callData.to)
      .set('from_number', callData.from_number)
      .set('caller_id', callData.caller_id);

    if (callData.patient_id) {
      params = params.set('patient_id', callData.patient_id);
    }

    return this.http.post<MakeCallResponse>(`${this.baseUrl}/exotel/call`, null, { params });
  }

  /**
   * Fetch real-time call details/status from Exotel for the frontend to monitor call progression (e.g., to start/stop call timers).
   * GET /api/v1/exotel/call/{call_sid}
   *
   * @param callSid - The Exotel CallSid returned by makeCall()
   */
  getCallDetails(callSid: string): Observable<ExotelCallDetailsResponse> {
    return this.http.get<ExotelCallDetailsResponse>(
      `${this.baseUrl}/exotel/call/${encodeURIComponent(callSid)}`
    );
  }

  /**
   * Poll the backend for the current status of an in-progress call.
   * Delegates to getCallDetails (GET /api/v1/exotel/call/{call_sid}).
   *
   * @param callSid - The Exotel CallSid returned by makeCall()
   */
  getCallStatus(callSid: string): Observable<ExotelCallDetailsResponse> {
    return this.getCallDetails(callSid);
  }

  /**
   * Manually trigger the call status callback endpoint (for testing/simulation).
   * Normally invoked directly by Exotel servers via the StatusCallback parameter.
   */
  postCallStatusCallback(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/exotel/callback/call-status`, payload);
  }
}
